import {CreateStoreServiceApiPayload} from "../../../../react-query-hooks/store/services/create-services/types";
import {UpdateStoreServiceApiPayload} from "../../../../react-query-hooks/store/services/update-store-service/types";
// import {CreateServiceApiPayload} from "../../../../react-query-hooks/products/create-product/types";
import {CreateStoreServiceFormValues} from "../reducers/create-service/types";
import {StoreServicePricingFormFieldsType} from "../../../../components/offering-form/services/pricing/types";
import {
  toPurchaseLimitPayload,
  toPurchaseLimitUpdatePayload,
} from "../../../../components/offering-form/general/settings/purchase-limit-payload";

/** The pricing slice of an offering form — all `toStoreServicePricingPayload` reads. */
type StoreServicePricingFormValues = Pick<
  StoreServicePricingFormFieldsType,
  "priceType" | "currencies" | "selected_currencies"
>;

/**
 * The Product Service leg of the Pricing Model translation.
 *
 * Product Services put EVERY currency row into `currencies[]`, base row included, and
 * have no base-level Minimum Price — unlike live events and booking services, which split
 * the base row into top-level scalars. See
 * `docs/adr/0002-pricing-slice-uniform-currency-rows.md`.
 *
 * Free sends nothing. Single Fee sends the amounts but no Minimum Price, so a minimum the
 * coach typed during a visit to Pay What You Want cannot leak out under a model that has
 * no meaning for it.
 */
export function toStoreServicePricingPayload(values: StoreServicePricingFormValues) {
  const isPayWhatYouWant = values.priceType === "pwyw";

  return {
    isPayWhatYouWant,
    currencies:
      values.priceType === "free"
        ? []
        : values.currencies.map((currency) => ({
            callPriceCurrency: currency.callPriceCurrency,
            callPrice: currency.callPrice,
            ...(isPayWhatYouWant ? {minCallPrice: currency.minCallPrice} : {}),
          })),
    enable_currency_conversion: values.priceType !== "free",
    selected_currencies:
      values.priceType === "free" ? [] : values.selected_currencies || [],
  };
}

export function createStoreServiceDto(
  values: CreateStoreServiceFormValues
): CreateStoreServiceApiPayload {
  const redirectType =
    values.redirectAfterPurchase === "custom-url"
      ? "url"
      : values.redirectAfterPurchase ?? "none";

  return {
    ...toStoreServicePricingPayload(values),
    description: values.description,
    hideFromProfile: values.hideFromProfile,
    name: values.name,
    request_phone_number: values.requestCustomerPhoneNumber,
    ...toPurchaseLimitPayload(values),
    url_redirect: redirectType === "url" ? values.url_redirect : null,
    redirect_type: redirectType,
    telegram_group_chat_id:
      redirectType === "telegram" ? values.telegramGroupId ?? null : null,
    delivery_timeframe_unit: values.deliveryTimeFrameUnit,
    delivery_timeframe_value: values.deliveryTimeFrameValue,
    bookingQuestions:
      values.bookingQuestions.length > 0
        ? values.bookingQuestions.map((q) => {
            if (
              q.answerType === "checkbox" ||
              q.answerType === "dropdown" ||
              q.answerType === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answer_type: q.answerType,
                options: q.options.map((o) => o.optionLabel),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answer_type: q.answerType,
              };
            }
          })
        : [],
  };
}

export function createEditStoreServiceDto(
  values: CreateStoreServiceFormValues,
  meta: {serviceId: number}
): UpdateStoreServiceApiPayload {
  const redirectType =
    values.redirectAfterPurchase === "custom-url"
      ? "url"
      : values.redirectAfterPurchase ?? "none";

  return {
    serviceId: meta.serviceId,
    ...toStoreServicePricingPayload(values),
    description: values.description,
    hideFromProfile: values.hideFromProfile,
    name: values.name,
    request_phone_number: values.requestCustomerPhoneNumber,
    // Re-submitting step one updates the service the wizard already created, so a cap
    // the coach has since removed has to be cleared rather than left unsaid.
    ...toPurchaseLimitUpdatePayload(values),
    url_redirect: redirectType === "url" ? values.url_redirect : null,
    redirect_type: redirectType,
    telegram_group_chat_id:
      redirectType === "telegram" ? values.telegramGroupId ?? null : null,
    delivery_timeframe_unit: values.deliveryTimeFrameUnit,
    delivery_timeframe_value: values.deliveryTimeFrameValue,
    bookingQuestions:
      values.bookingQuestions.length > 0
        ? values.bookingQuestions.map((q) => {
            if (
              q.answerType === "checkbox" ||
              q.answerType === "dropdown" ||
              q.answerType === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answer_type: q.answerType,
                options: q.options.map((o) => o.optionLabel),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answer_type: q.answerType,
              };
            }
          })
        : [],
  };
}
